export type RepeatMode = 'off' | 'all' | 'one'

export type EnqueueMode = 'after-current' | 'after-next' | 'after-album' | 'after-artist' | 'at-end'

export interface ReplayGainData {
  trackGain: number | null
  trackPeak: number | null
  albumGain: number | null
  albumPeak: number | null
}

export interface QueueItem {
  // Stable per-entry identity. `id` is the track id, which repeats when the same
  // track is queued twice, so it can't key a reorderable list on its own.
  uid: string
  id: number
  title: string
  artist: string
  artistId: number
  album: string
  albumId: number
  duration: number
  coverArt: string | null
  replayGain: ReplayGainData | null
  tempo: number | null
  keyName: string | null
  energy: number | null
  mood: {
    danceability: number | null
    aggression: number | null
    happiness: number | null
    party: number | null
    relaxation: number | null
    sadness: number | null
  } | null
  radioGenerated?: boolean
  isInterstitial?: boolean
  streamOverride?: string
}

export interface PlayOptions {
  startIndex?: number
  // Radio's dead-end recovery replaces the queue on purpose to reseed itself,
  // so it needs the one exemption from play() ending radio mode.
  keepRadio?: boolean
}

export interface AudioChannel {
  audio: HTMLAudioElement
  source: MediaElementAudioSourceNode | null
  gainNode: GainNode | null
  analyserNode: AnalyserNode | null
}

export interface PlayerState {
  queue: QueueItem[]
  currentIndex: number
  isPlaying: boolean
  shuffle: boolean
  repeat: RepeatMode
  volume: number
  muted: boolean
  duration: number
  crossfadeDuration: number
  replayGainEnabled: boolean
  replayGainPreampDb: number
  radioMode: boolean
  radioFollow: boolean
}

export const BASE_PLAYER_STATE: Omit<
  PlayerState,
  'crossfadeDuration' | 'replayGainEnabled' | 'replayGainPreampDb' | 'radioMode' | 'radioFollow'
> = {
  queue: [],
  currentIndex: -1,
  isPlaying: false,
  shuffle: false,
  repeat: 'off',
  volume: 0.8,
  muted: false,
  duration: 0,
}
